import { CommonModule } from '@angular/common'
import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, ViewEncapsulation } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar'
import { ActivatedRoute } from '@angular/router'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { QuizService } from 'src/app/service/data/quiz.service'
import { DdcQuizQuestionsComponent } from '../../shared/ddc/ddc-quiz-questions/ddc-quiz-questions.component'
import { Quiz, QuizResult } from '../../shared/models/main/quiz'

@Component({
  selector: 'app-quiz-detail',
  templateUrl: './quiz-detail-page.component.html',
  styleUrls: ['./quiz-detail-page.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    AngularMaterialsModules,
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    DdcQuizQuestionsComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class QuizDetailPageComponent implements OnInit {
  quiz: Quiz | undefined
  quizId: string = ''
  loading = true
  error: string | null = null
  isTaking = false
  result: QuizResult | null = null
  startTime: number = 0

  // Track selected answers
  private selectedAnswers = new Map<number, number>() // questionId -> answerId

  constructor(
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private quizService: QuizService,
  ) {}

  ngOnInit(): void {
    this.quizId = this.route.snapshot.paramMap.get('id') || ''
    if (this.quizId) {
      this.loadQuiz(this.quizId)
    }
  }

  async loadQuiz(quizId: string): Promise<void> {
    this.loading = true
    this.error = null

    try {
      const response = await this.quizService.get_quiz(quizId).toPromise()

      if (response && response.success && response.res) {
        this.quiz = response.res as Quiz
        this.isTaking = !this.quiz.result
      } else {
        this.error = response?.res || 'Failed to load quiz'
        this.snackBar.open(this.error, 'Dismiss', {
          duration: 5000,
        })
      }
    } catch (error: any) {
      this.error = error.error || 'Failed to load quiz'
      this.snackBar.open('Failed to load quiz. Please try again.', 'Dismiss', {
        duration: 5000,
      })
    } finally {
      this.loading = false
    }
  }

  onAnswerSelect(questionId: number, answerId: number): void {
    this.selectedAnswers.set(questionId, answerId)
  }

  onNextQuestion(): void {
    // Track navigation for timing
  }

  onPreviousQuestion(): void {
    // Track navigation for timing
  }

  onSubmitQuiz(): void {
    if (!this.quiz) return

    // Calculate time taken
    const timeTaken = Math.floor((Date.now() - this.startTime) / 1000)

    // Prepare answers for submission
    const answers = Array.from(this.selectedAnswers.entries()).map(([questionId, answerId]) => ({
      question_id: questionId,
      answer_id: answerId,
    }))

    // Submit to backend
    this.quizService.submit_quiz(this.quiz.id, answers).subscribe({
      next: (response: any) => {
        if (response.success && response.res) {
          // Parse the result from backend
          const quizResult: QuizResult = {
            score: response.res.score || 0,
            correct_count: response.res.correct_count || 0,
            total_questions: response.res.total_questions || this.quiz?.questions?.length || 0,
            time_taken: timeTaken,
            wrong_answered_questions: response.res.wrong_answered_questions || [],
          }
          this.result = quizResult
          this.isTaking = false

          this.snackBar.open('Quiz submitted successfully!', 'OK', {
            duration: 3000,
          })
        } else {
          this.snackBar.open('Failed to submit quiz: ' + response.res, 'OK', {
            duration: 5000,
          })
        }
      },
      error: (error: any) => {
        this.snackBar.open('Error submitting quiz: ' + (error.error || error.message), 'OK', {
          duration: 5000,
        })
      },
    })
  }

  onStartQuiz(): void {
    this.isTaking = true
    this.startTime = Date.now()
    this.selectedAnswers.clear()
  }

  onCancel(): void {
    if (confirm('Are you sure you want to quit this quiz? Your progress will be lost.')) {
      this.isTaking = false
    }
  }
}
